/**
 * Course-format helpers — the COURSE flavour of the Format editor's ONE
 * Targets list (#2400; course/ends unification, owner feedback 2026-09-10:
 * "there is no way to set the face for a course based format. plus course has
 * weird extra buttons like add targets, add nfaa field units which don't
 * belong.").
 *
 * A Format is an ordered series of TARGETS. Each row is a segment:
 * `{distance, unit, ends, arrows_per_end, face, alt_distances, faces}` —
 * exactly the shape `lib/customRoundStages.js` already edits for an
 * ends-based Format. "An ends-based stage is the same thing as a course
 * target"; a course just typically shoots fewer arrows at each of many
 * targets before walking on.
 *
 * Since the fix above, course mode uses the SAME Rule set cards (the
 * `course_only` rule sets from the `ruleSets` SSOT) and the SAME Faces
 * section as ends mode, so this module no longer owns a parallel "course
 * family" taxonomy, a family → face mapping, or the official NFAA unit fill.
 * What survives is the course-side DEFAULTS and the two rules ends mode has
 * no equivalent for:
 *
 *   - a new target row (1 end × the rule set's arrows per target),
 *   - WA field's face-size-by-distance rule,
 *   - splitting a legacy single block into one row per target,
 *   - the target COUNT / echo (Σ ends, not Σ rows),
 *   - `isCourseRoundType` — the ONE helper every surface uses to decide
 *     whether a row is walked as a course (targets) or shot as ends.
 */

import { defaultStage, totalArrows, maxScore } from "./customRoundStages";

// Arrows shot at one target, by rule set — the only per-rule-set default a
// course row needs now that the face comes from the Faces section. NFAA field
// and hunter shoot 4, WA field 3, 3D one (except the NFAA 2-arrow face, which
// is what its name says).
const COURSE_ARROWS_PER_TARGET = {
  NFAA_FIELD: 4,
  NFAA_HUNTER: 4,
  WA_FIELD: 3,
  THREE_D: 1,
};

// The unit a course of this rule set is conventionally walked in. NFAA field
// and hunter and the 3D circuits shoot yards; WA field is metric.
const COURSE_UNIT = {
  NFAA_FIELD: "yd",
  NFAA_HUNTER: "yd",
  WA_FIELD: "m",
  THREE_D: "yd",
};

/** Arrows a new target row defaults to: the rule set's own count, except that
 * the NFAA 2-arrow 3D face carries its count in the face itself. */
export function courseArrowsFor(ruleSet, face) {
  if (face === "3d-2arrow") return 2;
  return COURSE_ARROWS_PER_TARGET[ruleSet] ?? 1;
}

/** The unit a brand-new course row starts in for this rule set. */
export function courseUnitFor(ruleSet) {
  return COURSE_UNIT[ruleSet] ?? "m";
}

// The WA field faces that carry a SIZE (the 3-spot is a separate face, not a
// size bracket) — `waFieldFaceForDistance` only ever returns one of these.
const WA_FIELD_SIZED_FACES = ["wafield40", "wafield60", "wafield80"];

/**
 * Should a WA field row with no explicit face derive its face SIZE from its
 * own distance? Only when the Format actually allows several sizes — the
 * owner's case, "target 1 is a 122cm at 70m, target 2 a 40cm at 30m". A
 * Format that allows exactly one WA field face just uses it.
 */
export function waFieldAutoSize(ruleSet, allowedFaces) {
  if (ruleSet !== "WA_FIELD") return false;
  return (allowedFaces || []).filter((f) => WA_FIELD_SIZED_FACES.includes(f)).length > 1;
}

/**
 * WA field face size by target distance (WA rule, marked field course):
 * ≤ 10 m → 20 cm, ≤ 20 m → 40 cm, ≤ 35 m → 60 cm, else → 80 cm. The face
 * registry has no 20 cm single-spot WA field face — only wafield40/60/80
 * (+3spot) — so the ≤ 10 m and ≤ 20 m brackets both resolve to `wafield40`.
 * A yards distance is converted to metres (× 0.9144) before bracketing; a
 * null/unset distance falls back to `wafield40`.
 */
export function waFieldFaceForDistance(distance, unit) {
  if (distance == null) return "wafield40";
  const metres = unit === "yd" ? Number(distance) * 0.9144 : Number(distance);
  if (metres <= 20) return "wafield40";
  if (metres <= 35) return "wafield60";
  return "wafield80";
}

const WA_FIELD_FACE_SIZE_CM = { wafield40: 40, wafield60: 60, wafield80: 80, wafield3spot: 20 };

/** "40 cm" for a face code that carries a WA field size, else "". */
export function faceSizeLabelFor(face) {
  const cm = WA_FIELD_FACE_SIZE_CM[face];
  return cm ? `${cm} cm` : "";
}

// ── NFAA field / hunter per-target face SIZE (QA fix #2436, finding 1) ──────
// Unlike WA field, NFAA field and hunter each have exactly ONE face registry
// code (`nfaafield` / `nfaahunter`) covering every physical size — the 20 cm
// "spot" targets, the 35/50/65 cm bulls of the standard 14-target unit and a
// custom course are all the same face code, so a bigger/smaller target can
// only be expressed as a per-SEGMENT `face_size_cm` override, never a
// different face code (contrast `WA_FIELD_FACE_SIZE_CM` above). These are the
// four sizes the official NFAA table (round-catalog.md) actually uses.
const NFAA_FIELD_FACE_SIZES = [20, 35, 50, 65];

/** The face sizes a course row of this rule set may pick, or `null` when the
 * rule set has no per-target size concept (WA field varies FACE CODE instead;
 * 3D faces are size-free). */
export function courseFaceSizeOptions(ruleSet) {
  if (ruleSet === "NFAA_FIELD" || ruleSet === "NFAA_HUNTER") return NFAA_FIELD_FACE_SIZES;
  return null;
}

/**
 * The official NFAA face size (cm) for a target at `distance`/`unit`, per the
 * governing body's own bracket table. Cross-checked against every one of the
 * 28 targets in the standard Field AND Hunter 14-target units
 * (`NFAA_FIELD_TARGETS/NFAA_HUNTER_TARGETS`, `lib/scoring.js`) — both
 * disciplines share the SAME distance→size brackets (only the distances
 * assigned to each target number differ, known vs. estimated):
 *
 *   ≤ 12 yd → 20 cm   (the short/"spot" targets, e.g. Field's foot-marked
 *                       target 14 and Hunter's 11 yd target 14)
 *   ≤ 34 yd → 35 cm
 *   ≤ 54 yd → 50 cm
 *   > 54 yd  → 65 cm
 *
 * A null/unset distance (a target the archer hasn't set yet) defaults to
 * 35 cm — the same mid-size default `nfaafield`/`nfaahunter` already carry in
 * the face registry (`shared/face-definitions.json`), so an unset target's
 * chip pre-selects rather than showing nothing checked.
 */
export function nfaaFieldSizeForDistance(distance, unit) {
  if (distance == null) return 35;
  const yd = unit === "m" ? Number(distance) / 0.9144 : Number(distance);
  if (yd <= 12) return 20;
  if (yd <= 34) return 35;
  if (yd <= 54) return 50;
  return 65;
}

/**
 * A fresh COURSE target row. A course target is an ends-based stage with
 * `ends: 1` and the rule set's arrow count — "just typically courses shoot
 * fewer arrows at a target before moving on" (owner, 2026-09-10). `face` is
 * the face the row carries (null for a WA field row whose size follows its
 * own distance). `last` (the row being appended after, when there is one)
 * donates the unit so a list retuned to yards keeps growing in yards; the
 * distance is always left blank, because two targets on a course are rarely
 * at the same distance.
 */
export function defaultCourseRow({ ruleSet, face = null, last } = {}) {
  return defaultStage({
    distance: null,
    unit: last?.unit || courseUnitFor(ruleSet),
    // ALWAYS one end — a new target is ONE target (fix round 1). Inheriting
    // `last.ends` meant "+ Add target" on a legacy single block (`ends: 28`)
    // silently added another 28 targets.
    ends: 1,
    arrows_per_end: courseArrowsFor(ruleSet, face),
    face,
  });
}

/**
 * Expand ONE multi-end block row into `ends` single-target rows — the button
 * behind the "N targets in one block" hint (fix round 1, #2400). The block's
 * face, arrows, unit and (when it has one) distance ride along; a legacy
 * block's distance is null, so the split rows start blank, ready for
 * per-target distances.
 */
export function splitBlockIntoTargets(row) {
  const n = Math.max(1, Number(row?.ends) || 1);
  return Array.from({ length: n }, () => defaultStage({
    distance: row.distance ?? null,
    unit: row.unit || "m",
    ends: 1,
    arrows_per_end: Number(row.arrows_per_end) || 1,
    face: row.face ?? null,
  }));
}

/**
 * Is this catalog/personal row walked as a COURSE (a series of targets)
 * rather than shot as ends? Reads the server-derived `structure_type`
 * (#2321/#2401 backend: derived from the face's rule set, never
 * client-supplied). `"targets"` (the seeded WA field catalog rows) counts
 * too — those rows walk a course exactly like a `"course"` row, they just
 * predate this modelling.
 *
 * THE one helper for this question: `formatTileSummary`, `FormatTree`,
 * `StartSheet`, `wizardPresetCard` and `WizardStepFormat` all call it rather
 * than testing `structure_type === "course"` themselves (#2400).
 */
export function isCourseRoundType(rt) {
  return rt?.structure_type === "course" || rt?.structure_type === "targets";
}

/**
 * How many TARGETS a row list walks: Σ ends, not Σ rows (fix round 1). A
 * course row usually carries one end (one target), but a legacy single block
 * carries all of them (`{ends: 28}`) and the seeded WA field catalog rows
 * group three targets per distance stage (8 × 3 = 24) — counting rows there
 * would have claimed "1 target" and "8 targets".
 */
export function courseTargetCount(rows) {
  return (rows || []).reduce((sum, r) => sum + (Number(r.ends) || 0), 0);
}

/**
 * Live one-line echo for a course Format, e.g.
 * "28 targets · 112 arrows · 560 max". Rows must already carry their RESOLVED
 * face (the editor substitutes WA field's distance-derived face first).
 */
export function courseEchoSummary(rows) {
  const n = courseTargetCount(rows);
  const label = n === 1 ? "1 target" : `${n} targets`;
  return `${label} · ${totalArrows(rows)} arrows · ${maxScore(rows)} max`;
}
